import { Text } from '@/components/ui/text';
import { useEffect, useState, type FC } from 'react';

interface PremarketApprovalInputsProps {
  initialQuery: string;
  onQueryChange: (query: string, isValid: boolean) => void;
  showErrors: boolean;
  setShowErrors: (showErrors: boolean) => void;
}

export const PremarketApprovalInputs: FC<PremarketApprovalInputsProps> = ({
  initialQuery,
  onQueryChange,
  showErrors,
  setShowErrors,
}) => {
  const [productCode, setProductCode] = useState('');
  const [deviceName, setDeviceName] = useState('');
  const [applicantName, setApplicantName] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  const errorMessage =
    'At least one search criterion is required (product code, device name, applicant name, or date range)';

  // Parse initial query
  useEffect(() => {
    if (initialQuery) {
      const parts = initialQuery.split('|').map(p => p.trim());
      setProductCode(parts[0] || '');
      setDeviceName(parts[1] || '');
      setApplicantName(parts[2] || '');
      setStartDate(parts[3] || '');
      setEndDate(parts[4] || '');
    }
  }, [initialQuery]);

  // Build and notify query changes
  useEffect(() => {
    const buildQuery = () => {
      let query = productCode.trim();
      query += ` | ${deviceName.trim()}`;
      query += ` | ${applicantName.trim()}`;
      query += ` | ${startDate.trim()}`;
      query += ` | ${endDate.trim()}`;
      return query;
    };

    const query = buildQuery();
    // Query is valid if at least one field has a value
    const isValid =
      productCode.trim().length > 0 ||
      deviceName.trim().length > 0 ||
      applicantName.trim().length > 0 ||
      startDate.trim().length > 0 ||
      endDate.trim().length > 0;
    onQueryChange(query, isValid);
    if (isValid) {
      setShowErrors(false);
    }
  }, [
    productCode,
    deviceName,
    applicantName,
    startDate,
    endDate,
    onQueryChange,
  ]);

  return (
    <>
      {/* Product Code Input */}
      <div className="flex flex-col gap-1 w-full">
        <Text variant="body-sm" className="font-medium text-neutral-600">
          Product Code (optional)
        </Text>
        <input
          type="text"
          placeholder="e.g., NOF, DTB, or LWG"
          value={productCode}
          onChange={e => setProductCode(e.target.value.toUpperCase())}
          className="rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
        />
        {showErrors && (
          <Text variant="body-xs" color="danger">
            {errorMessage}
          </Text>
        )}
      </div>

      {/* Device Name Input */}
      <div className="flex flex-col gap-1 w-full">
        <Text variant="body-sm" className="font-medium text-neutral-600">
          Device Name (optional)
        </Text>
        <input
          type="text"
          placeholder="e.g., pacemaker, heart valve"
          value={deviceName}
          onChange={e => setDeviceName(e.target.value)}
          className="rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
        />
        {showErrors && (
          <Text variant="body-xs" color="danger">
            {errorMessage}
          </Text>
        )}
      </div>

      {/* Applicant Name Input */}
      <div className="flex flex-col gap-1 w-full">
        <Text variant="body-sm" className="font-medium text-neutral-600">
          Applicant Name (optional)
        </Text>
        <input
          type="text"
          placeholder="e.g., Medtronic, Abbott"
          value={applicantName}
          onChange={e => setApplicantName(e.target.value)}
          className="rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
        />
        {showErrors && (
          <Text variant="body-xs" color="danger">
            {errorMessage}
          </Text>
        )}
      </div>

      {/* Date Range Inputs */}
      <div className="grid grid-cols-2 gap-3">
        <div className="flex flex-col gap-1">
          <Text variant="body-sm" className="font-medium text-neutral-600">
            Start Date (optional)
          </Text>
          <input
            type="date"
            value={startDate}
            onChange={e => setStartDate(e.target.value)}
            className="rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
          />
        </div>
        <div className="flex flex-col gap-1">
          <Text variant="body-sm" className="font-medium text-neutral-600">
            End Date (optional)
          </Text>
          <input
            type="date"
            value={endDate}
            onChange={e => setEndDate(e.target.value)}
            className="rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
          />
        </div>
      </div>
    </>
  );
};
